import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { Translate } from 'react-localize-redux';
import classNames from 'classnames';
import css from './UserConfigurationShelf.css';
import { Button, Shelf, Spinner, TextField, WarningMessage } from '../../../components/widgets';
import { GET_PARTNER_ACCOUNTS, GET_PARTNER } from '../../queries';
import { partnerShape } from '../types';
import useOAuthFlow from '../../hooks/useOAuthFlow';
import AddAccountIcon from '../../../resources/svg/partner_config/add_account.svg';
import GuideIcon from '../../../resources/svg/guide.svg';
import LoginsTable from './LoginsTable';
import LoginCardsGroup from './LoginCardsGroup';

export const USER_CONFIGURATION_SHELF_DATA_TEST_ID = 'user-configuration-shelf';

export default function UserConfigurationShelf({ open = false, partner, onClose = () => {}, onDone = null }) {
    const { id: partnerId, name: partnerName, displayName: partnerDisplayName, logoPath: icon } = partner;

    const [searchText, setSearchText] = useState('');
    const { startOAuthFlow, error: oauthError, clearError } = useOAuthFlow(partnerId, partnerName, partnerDisplayName);

    const [doneState, setDoneState] = useState({ handleDone: onClose, disabled: !!onDone, loading: false });

    const partnerAccountsResponse = useQuery(GET_PARTNER_ACCOUNTS, {
        variables: { partnerId },
        skip: !open,
    });

    const partnerDetailsResponse = useQuery(GET_PARTNER, {
        variables: { partnerId },
        skip: !open,
    });
    const guideLink = partnerDetailsResponse.data?.partner?.guideLink;

    const accounts = partnerAccountsResponse.data?.partnerAccounts || [];
    const hasLoginAccounts = accounts.some(a => a.loginAccounts?.length > 0);

    const triggerOAuthFlow = useCallback(() => {
        clearError();
        startOAuthFlow();
    }, [clearError, startOAuthFlow]);

    const onDoneReady = useCallback(state => {
        setDoneState(state);
    }, []);

    const handleClose = () => {
        onClose();
    };

    const footerButtons = [
        {
            type: 'primaryButton',
            text: 'STATIC.BUTTONS.DONE',
            onClick: doneState.handleDone,
            disabled: doneState.disabled || doneState.loading,
            showSpinner: doneState.loading,
        },
    ];

    const getShelfContent = () => {
        if (partnerAccountsResponse.loading) {
            return <Spinner show expanded />;
        }

        if (partnerAccountsResponse.error) {
            return (
                <WarningMessage
                    show
                    type="error"
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.LOAD_ERROR"
                    showIcon
                />
            );
        }

        const ContentComponent = hasLoginAccounts ? LoginCardsGroup : LoginsTable;

        return (
            <div className={css.userConfigurationShelf}>
                <div className={css.headerSection}>
                    <div className={css.headerInfo}>
                        <h3 className={css.title}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.TITLE"
                                data={{ partnerName: partnerDisplayName }}
                            />
                        </h3>
                        <p className={css.description}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.DESCRIPTION"
                                data={{ partnerName: partnerDisplayName }}
                            />
                        </p>
                    </div>
                    <Button type="primary" onClick={triggerOAuthFlow} className={css.addUserButton}>
                        <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ADD_USER" />
                    </Button>
                </div>

                {oauthError && (
                    <WarningMessage
                        show
                        type="error"
                        message="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.SAVE_ERROR"
                        showIcon
                    />
                )}

                <div className={css.contentWrapper}>
                    <div className={css.searchSection}>
                        <TextField
                            placeholder="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.SEARCH_PLACEHOLDER"
                            value={searchText}
                            onChange={value => setSearchText(value)}
                            containerClass={css.searchInput}
                        />
                        {!guideLink ? null : (
                            <div className={css.guide} onClick={() => window.open(guideLink, '_blank', 'noreferrer')}>
                                <GuideIcon className={css.icon} />
                                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_GUIDE" />
                            </div>
                        )}
                    </div>

                    <div className={css.accountsSection}>
                        {accounts.length === 0 ? (
                            <div className={classNames(css.emptyState, css.clickable)} onClick={triggerOAuthFlow}>
                                <AddAccountIcon className={css.emptyStateIcon} />
                                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_ACCOUNTS" />
                            </div>
                        ) : (
                            <ContentComponent
                                accounts={accounts}
                                searchText={searchText}
                                partnerId={partnerId}
                                partnerName={partnerName}
                                partnerDisplayName={partnerDisplayName}
                                icon={icon}
                                triggerOAuthFlow={triggerOAuthFlow}
                                guideLink={guideLink}
                                onDoneReady={onDoneReady}
                                onClose={onClose}
                                onDone={onDone}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Shelf
            open={open}
            shelfSize="large"
            headerText="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION"
            headerImagePath={icon}
            enterAnimationDisabled={false}
            onClose={handleClose}
            dataTestId={USER_CONFIGURATION_SHELF_DATA_TEST_ID}
            footerNavigationButtons={footerButtons}
        >
            {getShelfContent()}
        </Shelf>
    );
}

UserConfigurationShelf.propTypes = {
    open: PropTypes.bool,
    partner: partnerShape.isRequired,
    onClose: PropTypes.func,
    onDone: PropTypes.func,
};
